import axios from "axios";
import { config } from "@/config";

const api = axios.create({ baseURL: config.apiUrl, timeout: 20000 });

// Admin key: stored locally, sent only on /admin/* requests.
export const ADMIN_KEY_STORAGE = "ttn_admin_key";
export const getAdminKey = () => localStorage.getItem(ADMIN_KEY_STORAGE) || "";
export const setAdminKey = (k) => localStorage.setItem(ADMIN_KEY_STORAGE, k || "");
export const clearAdminKey = () => localStorage.removeItem(ADMIN_KEY_STORAGE);

api.interceptors.request.use((cfg) => {
  if (cfg.url && cfg.url.includes("/admin")) {
    const k = getAdminKey();
    if (k) cfg.headers["x-admin-key"] = k;
  }
  return cfg;
});

// HARD BLOCK: if the backend says a user is blocked, log them out immediately and lock the app.
let _blockedHandled = false;
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (error?.response?.status === 403 && typeof detail === "string" && detail.startsWith("BLOCKED") && !_blockedHandled) {
      _blockedHandled = true;
      try {
        localStorage.removeItem("titan_wallet_address");
      } catch (e) {}
      window.alert(detail);
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const getConfig = () => api.get("/config").then((r) => r.data);
export const getDashboardStats = () => api.get("/dashboard/stats").then((r) => r.data);
export const getGlobalActivity = (limit = 15) =>
  api.get(`/activity/global?limit=${limit}`).then((r) => r.data);
export const getNonce = (address) =>
  api.get("/auth/nonce", { params: { address } }).then((r) => r.data);
export const verifySignature = (payload) =>
  api.post("/auth/verify", payload).then((r) => r.data);
export const getUser = (address) => api.get(`/user/${address}`).then((r) => r.data);
export const activateId = (payload) =>
  api.post("/activate", payload).then((r) => r.data);
export const getMe = (address) => api.get(`/me/${address}`).then((r) => r.data);
export const getHolders = (params) =>
  api.get("/holders", { params }).then((r) => r.data);
export const getTeam = (address) => api.get(`/team/${address}`).then((r) => r.data);
export const getPools = (address) => api.get(`/pools/${address}`).then((r) => r.data);

// ---- Admin panel ----
export const adminOverview = () => api.get("/admin/overview").then((r) => r.data);
export const adminAuthNonce = () => api.get("/admin/auth/nonce").then((r) => r.data);
export const adminAuthVerify = (payload) => api.post("/admin/auth/verify", payload).then((r) => r.data);
export const adminUsers = (params) => api.get("/admin/users", { params }).then((r) => r.data);
export const adminUserDetail = (address) => api.get(`/admin/user/${address}`).then((r) => r.data);
export const getRewardUser = (address) => api.get(`/reward/tree/user/${address}`).then((r) => r.data);
export const recalcRewards = () => api.post("/reward/tree/build").then((r) => r.data);

// ---- Custodial USDT: settings / deposits / wallet / withdrawals ----
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const submitDeposit = (payload) => api.post("/deposit/submit", payload).then((r) => r.data);
export const getWallet = (address) => api.get(`/wallet/${address}`).then((r) => r.data);
export const requestWithdraw = (payload) => api.post("/withdraw/request", payload).then((r) => r.data);
export const compound = (payload) => api.post("/compound", payload).then((r) => r.data);
export const getWithdrawHistory = (address) => api.get(`/withdraw/history/${address}`).then((r) => r.data);
export const getDepositHistory = (address) => api.get(`/deposit/history/${address}`).then((r) => r.data);
export const getTopDepositors = () => api.get("/top-depositors").then((r) => r.data);
export const getIncomeHistory = (address, category = "all", page = 1) =>
  api.get(`/income/history/${address}`, { params: { category, page } }).then((r) => r.data);
export const adminWithdrawals = (params) => api.get("/admin/withdrawals", { params }).then((r) => r.data);
export const adminMarkWithdraw = (id, payload) => api.post(`/admin/withdraw/${id}/mark`, payload).then((r) => r.data);
export const adminDeposits = () => api.get("/admin/deposits").then((r) => r.data);
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminVerifyAuto = (payload) => api.post("/admin/user/verify-auto", payload).then((r) => r.data);
export const adminAutoUsers = () => api.get("/admin/auto-users").then((r) => r.data);
export const adminAdjust = (payload) => api.post("/admin/user/adjust", payload).then((r) => r.data);
export const adminBlock = (payload) => api.post("/admin/user/block", payload).then((r) => r.data);
export const adminSetSettings = (payload) => api.post("/admin/settings", payload).then((r) => r.data);

export default api;
